import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import cartApi from '../../api/cartApi';
import '../Profile.css';
import couponsApi from '../../api/couponsApi';

const Cart = () => {
  const { user, removeFromCart, loadCart } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [selectedCouponId, setSelectedCouponId] = useState(0);
  const [payLoading, setPayLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [couponList, setCouponList] = useState([]);
  const [showCouponList, setShowCouponList] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/', { replace: true }); return; }
    if (user?.id) {
      setLoading(true);
      loadCart().finally(() => setLoading(false));
      cartApi.fetchCoupons(user.id).then(setCouponList).catch(() => setCouponList([]));
    }
  }, [user?.id, navigate]);

  if (!user) return null;

  if (loading) return <div style={{ padding: 20, textAlign: 'center' }}>טוען מוצרים בסל...</div>;

  const items = user?.cart || [];


  const handleRemove = async (e, itemId) => {
    e.stopPropagation();
    await removeFromCart(itemId);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponResult(null);
    setDiscount(0);
    try {
      const itemIds = items.map(it => it.id);
      const res = await couponsApi.applyCoupon({ userId: user.id, couponCode, itemIds });
      setCouponResult(res);
      if (res && res.valid && res.valid.discount) {
        const discountValue = Math.abs(Number(res.valid.discount));
        setDiscount(discountValue);
      }
      if (res && res.valid && res.valid.couponId) setSelectedCouponId(res.valid.couponId);
    } catch (e) {
      setCouponResult({ valid: false, message: 'שגיאה בבדיקת קופון' });
      setDiscount(0);
    }
  };

  const handleCheckout = async () => {
    if (!items || items.length === 0) return;
    try {
      setPayLoading(true);
      setMessage(null);
      const orderItems = items.map(it => ({
        item_id: it.id,
        name: it.name,
        price: it.sale_price !== null && it.sale_price !== undefined ? it.sale_price : it.price,
        quantity: 1
      }));
      
      const res = await cartApi.createOrderAndGetPaymentUrl({
        userId: user.id,
        items: orderItems,
        coupon_id: selectedCouponId,
        coupon_discount: -Math.abs(discount)
      });

      if (res && res.paymentUrl) {
        window.open(res.paymentUrl, '_self');
      } else {
        setMessage('לא התקבל כתובת תשלום');
      }
    } catch (err) {
      setMessage((err && err.message) || 'שגיאה בתשלום');
    } finally {
      setPayLoading(false);
    }
  };

  const calculateOriginalTotal = () => {
    return items.reduce((sum, it) => sum + (Number(it.price) || 0), 0);
  };

  const calculateSaleTotal = () => {
    return items.reduce((sum, it) => {
      const price = it.sale_price !== null && it.sale_price !== undefined ? it.sale_price : it.price;
      return sum + (Number(price) || 0);
    }, 0);
  };

  const calculateSaleDiscount = () => {
    return calculateOriginalTotal() - calculateSaleTotal();
  };

  const calculateFinalTotal = () => {
    return Math.max(0, calculateSaleTotal() - (Number(discount) || 0));
  };

  return (
    <div className="cart-container">
      <div className="cart-header">
      </div>

      {items.length === 0 && (
        <div className="cart-empty">
          <div className="empty-icon">🛒</div>
          <div className="empty-text">הסל שלך ריק</div>
          <div className="empty-subtext">הוסף מוצרים לסל כדי להתחיל בקניות</div>
        </div>
      )}

      {items.length > 0 && (
        <div className="cart-main-layout">
          <div className="cart-items-section">
            <div className="cart-items-container">
              {items.map((it, index) => (
                <div key={it.id}>
                  <div 
                    className="cart-item-row"
                    onClick={() => navigate(`/item/${it.id}`)}
                    onMouseEnter={() => setHoveredItem(it.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <div className="cart-item-thumb">
                      <img src={it.url || ''} alt={it.name} />
                    </div>
                    <div className="cart-item-name">{it.name}</div>
                    <div className="cart-item-price">
                      {it.sale_price !== null && it.sale_price !== undefined ? (
                        <>
                          <span className="original-price-small">₪{it.price}</span>
                          ₪{it.sale_price}
                        </>
                      ) : (
                        `₪${it.price}`
                      )}
                    </div>
                    {hoveredItem === it.id && (
                      <button 
                        className="cart-remove-x-btn" 
                        onClick={(e) => handleRemove(e, it.id)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {index < items.length - 1 && <div className="cart-item-divider" />}
                </div>
              ))}
            </div>
          </div>

          <div className="cart-summary-section">
            <div className="cart-summary-box">
              <div className="summary-row">
                <span className="summary-label">סך פריטים:</span>
                <span className="summary-value">{items.length}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">מחיר:</span>
                <span className="summary-value">₪{calculateOriginalTotal().toFixed(2)}</span>
              </div>
              {calculateSaleDiscount() > 0 && (
                <div className="summary-row discount-row">
                  <span className="summary-label">הנחה:</span>
                  <span className="summary-value">₪{calculateSaleDiscount().toFixed(2)}-</span>
                </div>
              )}
              {discount > 0 && (
                <div className="summary-row discount-row">
                  <span className="summary-label">הנחת קופון:</span>
                  <span className="summary-value">₪{discount.toFixed(2)}-</span>
                </div>
              )}
              <div className="summary-row total-row">
                <span className="summary-label">סכום סופי:</span>
                <span className="summary-value">₪{calculateFinalTotal().toFixed(2)}</span>
              </div>
            </div>

            <div className="coupon-input-box">
              <input
                type="text"
                placeholder="יש לך קופון? תכניסי אותו כאן"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                onFocus={() => setShowCouponList(!showCouponList)}
                onBlur={() => setTimeout(() => setShowCouponList(false), 300)}
                className="coupon-input"
              />
              <button onClick={handleApplyCoupon} className="coupon-apply-btn">
                אישור
              </button>
              {showCouponList && couponList.length > 0 && (
                <div className="coupon-dropdown">
                  {couponList.map(c => (
                    <div
                      key={c.id}
                      className="coupon-dropdown-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setCouponCode(c.coupon_code);
                        setShowCouponList(false);
                      }}
                    >
                      {c.coupon_code}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button className="checkout-payment-btn" onClick={handleCheckout} disabled={payLoading}>
              {payLoading ? 'מעבר לתשלום...' : 'לתשלום ← '}
            </button>
            {message && <div className="payment-error-message">{message}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
